import { UIButton, UIColor, UIView } from "uicore-ts"
import { RHAltModeKeyable, RHAltModeKeyHandler } from "./Keyboard navigation/RHAltModeKeyHandler"
import { RHColor } from "./RHColor"
import { RHButtons } from "./Utils_slop/RHDesignTokens"


// MARK: - Types
export type RHBarButtonVariant = "default" | "callToAction" | "outlineOnDarkBackground"


// MARK: - RH Bar Button

interface RHBarButtonColors {
    normal: UIColor
    hovered: UIColor
    highlighted: UIColor
    selected: UIColor
}

// The title and background colors a button variant uses in each state.
interface RHBarButtonColorConfig {
    titleLabel: RHBarButtonColors
    background: RHBarButtonColors
}


export class RHBarButton extends UIButton implements RHAltModeKeyable {
    
    _variant: RHBarButtonVariant = "default"
    _altModeKeyHandler?: RHAltModeKeyHandler
    
    constructor(elementID?: string) {
        
        super(elementID, "button");
        
        // Configure Static CSS (once) using the cast workaround
        (this as RHBarButton).configureWithObject({
            style: {
                borderRadius: RHButtons.barBorderRadius,
                padding: RHButtons.barPadding,
                fontFamily: RHButtons.barFontFamily,
                fontSize: RHButtons.barFontSize,
                textDecoration: "none",
                // Explicit transitions ONLY for visual properties.
                transition: "background-color 0.15s ease," +
                    " color 0.15s ease, " +
                    "border-color 0.15s ease, " +
                    "border-width 0.15s ease"
            }
        })
        
        // Initialize colors for default variant
        this.refreshColorConfiguration()
        
        this.updateContentForCurrentState()
        
    }
    
    
    // MARK: - Alt Mode Key
    
    /**
     * Assign a single letter (e.g. `"A"`) to register this button as an
     * alt-mode keyboard shortcut. The button draws its own badge when alt mode
     * is active and fires when that letter is pressed.
     *
     * Set to `undefined` to remove the shortcut.
     *
     * When using `RHBarLinkButton`, set `altModeKey` on the link-button wrapper
     * (it forwards the call here with the correct `displayView`).
     */
    get altModeKey(): string | undefined {
        return this._altModeKeyHandler?.altKey
    }
    
    set altModeKey(key: string | undefined) {
        this._setAltModeKey(key, undefined)
    }
    
    /**
     * Internal setter that lets RHBarLinkButton supply the outer wrapper view
     * as the display view for the badge, so the badge is positioned over the
     * full link-button frame rather than over the inner RHBarButton.
     */
    _setAltModeKey(key: string | undefined, displayView: UIView | undefined) {
        if (this._altModeKeyHandler) {
            this._altModeKeyHandler.deregister()
            this._altModeKeyHandler = undefined
        }
        if (key) {
            this._altModeKeyHandler = new RHAltModeKeyHandler(key, this, displayView)
        }
    }
    
    
    // MARK: - Color Management
    
    set variant(value: RHBarButtonVariant) {
        this._variant = value
        this.refreshColorConfiguration()
        this.updateContentForCurrentState()
    }
    
    get variant() {
        return this._variant
    }
    
    
    refreshColorConfiguration() {
        
        let colorConfig: RHBarButtonColorConfig
        
        if (this.variant === "default") {
            
            // --- Default Variant ---
            colorConfig = {
                titleLabel: {
                    normal: RHColor.dark,
                    hovered: RHColor.primary,
                    highlighted: RHColor.primary,
                    selected: RHColor.warningOrange
                },
                background: {
                    normal: UIColor.transparentColor,
                    hovered: RHColor.primary.colorWithAlpha(0.1),
                    highlighted: RHColor.primary.colorWithAlpha(0.2),
                    selected: RHColor.warningOrange.colorWithAlpha(0.05)
                }
            }
            
        }
        else if (this.variant === "outlineOnDarkBackground") {
            
            // --- Outline Variant ---
            colorConfig = {
                titleLabel: {
                    normal: UIColor.whiteColor,
                    hovered: UIColor.whiteColor,
                    highlighted: UIColor.whiteColor,
                    selected: RHColor.warning
                },
                background: {
                    normal: UIColor.transparentColor,
                    hovered: UIColor.whiteColor.colorWithAlpha(0.15),
                    highlighted: UIColor.whiteColor.colorWithAlpha(0.25),
                    selected: RHColor.warning.colorWithAlpha(0.05)
                }
            }
            
        }
        else {
            
            // --- Call To Action Variant ---
            colorConfig = {
                titleLabel: {
                    normal: UIColor.whiteColor,
                    hovered: UIColor.whiteColor,
                    highlighted: UIColor.whiteColor,
                    selected: RHColor.warning
                },
                background: {
                    normal: RHColor.callToActionColor,
                    hovered: RHColor.callToActionColor.colorByMultiplyingRGB(0.9),
                    highlighted: RHColor.callToActionColor.colorByMultiplyingRGB(0.8),
                    selected: RHColor.warning.colorWithAlpha(0.05)
                }
            }
            
        }
        
        // Use configureWithObject to update colors without replacing the object
        (this as RHBarButton).configureWithObject({ colors: colorConfig })
        
    }
    
    
    // MARK: - State Updates (CSS Only)
    
    override updateContentForNormalState() {
        
        super.updateContentForNormalState()
        
        if (this.variant === "outlineOnDarkBackground") {
            this.style.border = "1px solid rgba(255, 255, 255, 0.3)"
        }
        else {
            this.style.border = "none"
        }
        
        this.titleLabel.style.fontWeight = RHButtons.barFontWeight
        
    }
    
    
    override updateContentForHoveredState() {
        
        super.updateContentForHoveredState()
        
        if (this.variant === "outlineOnDarkBackground") {
            this.style.border = "1px solid rgba(255, 255, 255, 0.5)"
        }
        else {
            this.style.border = "none"
        }
        
        this.titleLabel.style.fontWeight = RHButtons.barFontWeight
        
    }
    
    
    override updateContentForHighlightedState() {
        
        super.updateContentForHighlightedState()
        
        if (this.variant === "outlineOnDarkBackground") {
            this.style.border = "1px solid rgba(255, 255, 255, 0.7)"
        }
        else {
            this.style.border = "none"
        }
        
        this.titleLabel.style.fontWeight = RHButtons.barFontWeight
        
    }
    
    
    override updateContentForSelectedState() {
        
        super.updateContentForSelectedState()
        
        if (this.variant === "outlineOnDarkBackground") {
            this.style.border = `2px solid ${RHColor.warning.stringValue}`
        }
        else {
            this.style.border = `2px solid ${RHColor.warningOrange.stringValue}`
        }
        
        this.titleLabel.style.fontWeight = RHButtons.barFontWeight
        
    }
    
}
